import { useEnrollCourseMutation } from "@api/course";
import CourseCard from "@components/CourseCard";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { useLeaveCourseModal } from "@components/Modal/LeaveCourseModal";
import {
  ArrowRightCircleIcon,
  ArrowRightEndOnRectangleIcon,
  ArrowRightStartOnRectangleIcon,
  PencilSquareIcon,
} from "@heroicons/react/16/solid";
import { useIsUserRole } from "@hooks/auth";
import { useToastEffect } from "@hooks/hooks";
import { CourseUserDetailsDTO } from "@onetutor/shared/dto";
import { useEnrollCourseModal } from "@pages/home/components/EnrollCourseModal";
import { buildQueryString } from "@utils/api.util";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

interface SearchStudentCourseCardProps {
  course: CourseUserDetailsDTO;
  setSearchQuery?: (query: string) => void;
}

function SearchStudentCourseCard(props: SearchStudentCourseCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(["toast", "coursePage", "commons"]);
  const { t: tCoursePage } = useTranslation("coursePage");
  const { t: tCommon } = useTranslation("commons");
  const isAdmin = useIsUserRole("admin");

  const { LeaveCourseModal, openLeaveCourseModal } = useLeaveCourseModal({
    courseId: props.course.id,
    courseName: props.course.name,
  });

  const { EnrollCourseModal, openEnrollModal } = useEnrollCourseModal(
    (password: string) => {
      enrollCourse({ courseId: props.course.id, password });
    },
  );

  const [enrollCourse, { isError, isSuccess, error }] =
    useEnrollCourseMutation();

  useToastEffect({
    isError,
    isSuccess,
    error,
    successMessage: t("courseCard.successEnroll", {
      courseName: props.course.name,
      ns: "toast",
    }),
  });

  const openCourse = () => {
    navigate(`course/${props.course.id}`);
  };

  const editCourse = () => {
    const path = buildQueryString(`course/${props.course.id}/manage`, {
      edit: true,
    });
    navigate(path);
  };

  const courseHandler = () => {
    if (props.course.isEnrolled) {
      openLeaveCourseModal();
    } else {
      if (props.course.isPasswordProtected) {
        openEnrollModal();
      } else {
        enrollCourse({ courseId: props.course.id });
      }
    }
  };

  const renderButtons = () => {
    return (
      <>
        {props.course.isEnrolled ? (
          <IconWithTooltip
            icon={
              <ArrowRightStartOnRectangleIcon className="h-6 w-6 text-error sm:h-7 sm:w-7" />
            }
            tooltip={tCoursePage("leave")}
            handler={courseHandler}
            tooltipPosition="left"
          />
        ) : (
          <IconWithTooltip
            icon={
              <ArrowRightEndOnRectangleIcon className="h-6 w-6 text-success sm:h-7 sm:w-7" />
            }
            tooltip={tCoursePage("enroll")}
            handler={courseHandler}
            tooltipPosition="left"
          />
        )}
        {isAdmin && (
          <>
            <IconWithTooltip
              icon={<PencilSquareIcon className="h-5 w-5 sm:h-7 sm:w-7" />}
              tooltip={tCommon("edit")}
              handler={editCourse}
              className="btn btn-ghost btn-circle hidden h-fit w-fit p-1 sm:block"
              tooltipPosition="left"
            />
            <IconWithTooltip
              icon={<ArrowRightCircleIcon className="h-5 w-5 sm:h-7 sm:w-7" />}
              tooltip={tCoursePage("goToCourse")}
              handler={openCourse}
              className="btn btn-ghost btn-circle hidden h-fit w-fit p-1 sm:block"
              tooltipPosition="left"
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <CourseCard course={props.course} showUniversityBadge={isAdmin}>
        {renderButtons()}
      </CourseCard>
      <LeaveCourseModal />
      <EnrollCourseModal />
    </>
  );
}

export default SearchStudentCourseCard;
